import React, { Component } from 'react';
import { Button, Col, Modal } from 'react-bootstrap';
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { MoveOutProrateBanner } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { FormattedMessage, injectIntl } from 'react-intl';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { submit } from 'redux-form';
import { propOr, pathOr } from 'ramda';
import { FORM_NAME } from './constants';
import messages from './messages';
import { createTransaction } from './actions';
import CreateTransactionForm from './CreateTransactionForm';
import SubmitButton from '../SubmitButton';
import {
  transactionCodesProvider,
  getFilteredTransactionCodes,
} from '../../utils/transaction-helpers';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { v4 as uuid } from 'uuid';

type AppState = {
  form: Object,
  actions: Object,
  frNames: string[],
};

type Props = AppState & {
  customerId: string,
  unit: ?Object,
  transactionTypes: Array<Object>,
  transactionCodes: Array<Object>,
  subjournals: Array<Object>,
  residents: Array<Object>,
  propertyName: string,
  status: string,
  priorBalance: number,
  priorSecurityBalance: number,
  priorSubjournalBalances: Object,
  onClose: Function,
  show: boolean,
  frNames: string[],
  customerStatus: string,
  fasDone: boolean,
  residentId: ?string,
  ledgerReset: ?Function,
  filterReversed: boolean,
  isSubmitting: boolean,
  isAllCommercial: boolean,
  camPools?: Array<Object>,
  propertyFiscalPeriod: Object,
  householdHasMoveOutProratedTransactions: boolean,
  intl: Object,
  flags?: Object,
  property?: Object,
  hasRentChargesPermission?: boolean,
};

type State = {
  showCancelConfirmation: boolean,
  idempotencyId: string,
};

export class CreateTransaction extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      showCancelConfirmation: false,
      idempotencyId: uuid(),
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.show && this.props.show) {
      this.setState({ idempotencyId: uuid() });
    }
  }

  setCancelConfirmation = (visible: boolean) => {
    this.setState({
      showCancelConfirmation: visible,
    });
  };

  showConfirmation = () => this.setCancelConfirmation(true);

  hideConfirmation = () => this.setCancelConfirmation(false);

  handleClose = () => {
    const {
      form: { anyTouched },
      onClose,
    } = this.props;
    const { showCancelConfirmation } = this.state;
    if (anyTouched && !showCancelConfirmation) {
      this.showConfirmation();
      return;
    }
    this.hideConfirmation();
    if (onClose) onClose();
  };

  submitValid = () => {
    const { form } = this.props;

    return !form?.syncErrors;
  };

  submit = (evt: Object) => {
    const {
      actions: { submit },
    } = this.props;
    submit(FORM_NAME);
  };

  handleSubmit = () => {
    const {
      customerId,
      unit,
      form: {
        values: {
          code,
          amount,
          date,
          receivedFrom,
          documentNumber,
          note,
          camPool,
          applyToCamPool,
        },
      },
      actions: { createTransaction },
      onClose,
      residentId,
      ledgerReset,
      filterReversed,
    } = this.props;
    const { idempotencyId } = this.state;
    const camAllocationsId =
      applyToCamPool === true && camPool && camPool !== 'default'
        ? camPool
        : undefined;
    const transaction = {
      prospectCustomerId: customerId,
      propertyTransactionCodeId: code,
      date: date.toDate(),
      amount,
      receivedFrom,
      documentNumber,
      unitId: unit ? unit.id : undefined,
      note,
      customerStatus: this.props.customerStatus,
      camAllocationsId,
      idempotencyId,
    };
    createTransaction(transaction, residentId, filterReversed);
    if (ledgerReset) ledgerReset();
    if (onClose) onClose();
  };

  renderCancelConfirmation = () => {
    return (
      <div className="modal-confirm">
        <Typography variant={'headlineMedium'} padding={2} component="h1">
          <FormattedMessage {...messages.cancelConfirmationHeader} />
        </Typography>
        <Col xs={12} sm={6}>
          <Button
            bsStyle="primary"
            className="pull-right"
            onClick={this.handleClose}
          >
            <FormattedMessage {...messages.yes} />
          </Button>
        </Col>
        <Col xs={12} sm={6}>
          <Button
            bsStyle="default"
            className="pull-left"
            onClick={this.hideConfirmation}
          >
            <FormattedMessage {...messages.no} />
          </Button>
        </Col>
      </div>
    );
  };

  render() {
    const {
      fasDone,
      unit,
      transactionCodes,
      transactionTypes,
      subjournals,
      residents,
      propertyName,
      status,
      priorBalance,
      priorSecurityBalance,
      priorSubjournalBalances,
      show,
      isSubmitting,
      isAllCommercial,
      propertyFiscalPeriod,
      flags,
      property,
      hasRentChargesPermission,
      hasNonRentChargesPermission,
    } = this.props;

    const tCodes = transactionCodesProvider(
      this.props.customerStatus,
      transactionCodes || [],
      fasDone,
    );
    const isCommercial = unit?.floorPlan?.isCommercial ?? false;
    // Filter out automated rent codes based on feature flags and permissions

    const filteredTCodes =
      flags && property
        ? getFilteredTransactionCodes(tCodes, {
            isMvpEnabled: flags.automateMonthlyLeaseTransactionsMvp || false,
            isV2Enabled: flags.automateMonthlyLeaseTransactionsV2 || false,
            property,
            hasRentChargesPermission: hasRentChargesPermission || false,
            filterMLTCodes: false,
            isCommercial,
            hasNonRentChargesPermission: hasNonRentChargesPermission || false,
            automatedLeaseChargesFlagEnabled:
              flags.automatedLeaseChargesP1 || false,
          })
        : tCodes;

    return (
      <Modal
        className="transaction"
        bsSize="small"
        show={show}
        autoFocus
        onHide={this.handleClose}
      >
        <Modal.Header closeButton>
          <i className="icon et-money" />
          <h1>
            <FormattedMessage {...messages.title} />
          </h1>
        </Modal.Header>
        <Modal.Body>
          {this.state.showCancelConfirmation && this.renderCancelConfirmation()}
          <div
            className="scrollable-modal__content row"
            style={this.state.showCancelConfirmation ? { display: 'none' } : {}}
          >
            <div className="container-fluid">
              {this.props.householdHasMoveOutProratedTransactions ? (
                <MoveOutProrateBanner intl={this.props.intl} />
              ) : null}
              <CreateTransactionForm
                formValues={propOr({}, 'values', this.props.form)}
                frNames={this.props.frNames}
                onSubmit={this.handleSubmit}
                priorBalance={priorBalance}
                priorSecurityBalance={priorSecurityBalance}
                priorSubjournalBalances={priorSubjournalBalances}
                propertyName={propertyName}
                residents={residents}
                status={status}
                subjournals={subjournals}
                transactionCodes={filteredTCodes}
                transactionTypes={transactionTypes}
                unit={unit}
                isAllCommercial={isAllCommercial}
                camPools={this.props.camPools}
                propertyFiscalPeriod={propertyFiscalPeriod}
                flags={flags}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          className={this.state.showCancelConfirmation ? 'disabled ' : ''}
        >
          <div className="row">
            <div className="col-xs-6">
              <Button
                type="button"
                bsStyle="default"
                className="pull-right"
                onClick={this.handleClose}
              >
                <FormattedMessage {...messages.cancel} />
              </Button>
            </div>
            <div className="col-xs-6">
              <SubmitButton
                bsStyle="primary"
                className="pull-left"
                disabled={!this.submitValid()}
                clickHandler={this.submit}
                isSubmitting={isSubmitting}
              >
                <FormattedMessage {...messages.post} />
              </SubmitButton>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = ({ form, createTransaction, app }) => {
  const userPermissions = pathOr([], ['currentUser', 'permissions'], app);
  const hasRentChargesPermission =
    userPermissions.find(
      (permission) => permission.scope === 'rent-charges-create-edit-reverse',
    ) !== undefined;

  const hasNonRentChargesPermission =
    userPermissions.find(
      (permission) => permission.scope === 'non-rent-charges-crud',
    ) !== undefined;
  return {
    form: form[FORM_NAME],
    isSubmitting: createTransaction.isSubmitting,
    property: app.selectedProperty,
    hasRentChargesPermission,
    hasNonRentChargesPermission,
  };
};
const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      submit,
      createTransaction,
    },
    dispatch,
  ),
});

export default withLDConsumer()(
  injectIntl(connect(mapStateToProps, mapDispatchToProps)(CreateTransaction)),
);
